
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Grid } from "@/components/ui/grid";
import { Baby, Calendar, Pill, Clock } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

// Define clear types for our data to avoid excessive type instantiation
interface GestationData {
  id: string;
  user_id: string;
  pregnancy_start_date: string;
  last_checkup_date: string | null;
  next_checkup_date: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

interface PeriodData {
  id: string;
  user_id: string;
  last_period_date: string;
  cycle_length: number;
  period_duration: number;
  created_at: string;
  updated_at: string;
}

// Updated to match the actual structure from the database
interface Medication {
  id: string;
  user_id: string;
  name: string;
  dosage: string;
  active: boolean;  // instead of status
  frequency: string;
  start_date: string;
  end_date: string;
  interactions?: string[];
  side_effects?: string[];
  notes?: string;
  created_at: string;
  updated_at: string;
  expiry_date?: string;
  last_notification_sent?: string;
  reminder_time?: string[];
}

interface JournalEntry {
  id: string;
  user_id: string;
  date: string;
  mood: string;
  symptoms?: string[];
  notes?: string;
  created_at: string;
  updated_at: string;
}

export const UserHealthDashboard = () => {
  const { data: gestationData } = useQuery({
    queryKey: ["gestation-tracking"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      
      const { data } = await supabase
        .from("gestation_tracking")
        .select("*")
        .eq("user_id", user.id)
        .single();
      
      return data as GestationData | null;
    }
  });

  const { data: periodData } = useQuery({
    queryKey: ["period-tracking"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      
      const { data } = await supabase
        .from("period_tracking")
        .select("*")
        .eq("user_id", user.id)
        .single();
      
      return data as PeriodData | null;
    }
  });

  const { data: medications } = useQuery({
    queryKey: ["active-medications"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];
      
      const { data } = await supabase
        .from("medications")
        .select("*")
        .eq("user_id", user.id)
        .eq("active", true);
      
      return data as Medication[] || [];
    }
  });

  const { data: recentEntries } = useQuery({
    queryKey: ["recent-journal-entries"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];
      
      const { data } = await supabase
        .from("health_journal")
        .select("*")
        .eq("user_id", user.id)
        .order("date", { ascending: false })
        .limit(3);
      
      return data as JournalEntry[] || [];
    }
  });

  // Calculate weeks based on pregnancy_start_date instead of conception_date
  const gestationWeeks = gestationData ? 
    Math.floor((new Date().getTime() - new Date(gestationData.pregnancy_start_date).getTime()) / (1000 * 60 * 60 * 24 * 7)) : 
    null;

  // Calculate next period date if period data exists
  const nextPeriodDate = periodData ? 
    new Date(new Date(periodData.last_period_date).getTime() + (periodData.cycle_length * 24 * 60 * 60 * 1000)) : 
    null;

  return (
    <Grid className="grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {/* Gestation card */}
      <Card className="bg-gradient-to-br from-teal-50 to-emerald-50 shadow-sm border-muted">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-teal-800 flex items-center gap-2">
            <Baby className="h-4 w-4 text-teal-600" />
            Suivi de grossesse
          </CardTitle>
        </CardHeader>
        <CardContent>
          {gestationData ? (
            <div className="space-y-1">
              <div className="text-3xl font-bold text-teal-800">
                {gestationWeeks} <span className="text-lg font-medium">semaines</span>
              </div>
              <p className="text-xs text-teal-600">
                Début: {new Date(gestationData.pregnancy_start_date).toLocaleDateString('fr-FR')}
              </p>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Aucune note</p>
          )}
        </CardContent>
      </Card>

      {/* Period tracking card */}
      <Card className="bg-gradient-to-br from-pink-50 to-rose-50 shadow-sm border-muted">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-pink-800 flex items-center gap-2">
            <Calendar className="h-4 w-4 text-pink-600" />
            Suivi des règles
          </CardTitle>
        </CardHeader>
        <CardContent>
          {periodData ? (
            <div className="space-y-1">
              <p className="text-sm text-pink-700">Dernières règles</p>
              <div className="text-xl font-bold text-pink-800">
                le {format(new Date(periodData.last_period_date), "d MMMM yyyy", { locale: fr })}
              </div>
              <p className="text-xs text-pink-600">
                Cycle de {periodData.cycle_length} jours
              </p>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Aucune donnée</p>
          )}
        </CardContent>
      </Card>

      {/* Medications card */}
      <Card className="bg-gradient-to-br from-amber-50 to-yellow-50 shadow-sm border-muted">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-amber-800 flex items-center gap-2">
            <Pill className="h-4 w-4 text-amber-600" />
            Médicaments actifs
          </CardTitle>
        </CardHeader>
        <CardContent>
          {medications && medications.length > 0 ? (
            <div className="space-y-1">
              <div className="text-3xl font-bold text-amber-800">
                {medications.length} <span className="text-lg font-medium">traitement{medications.length > 1 ? 's' : ''}</span>
              </div>
              <p className="text-xs text-amber-600">
                en cours
              </p>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Aucun traitement actif</p>
          )}
        </CardContent>
      </Card>

      {/* Recent activity card */}
      <Card className="bg-gradient-to-br from-violet-50 to-purple-50 shadow-sm border-muted">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-violet-800 flex items-center gap-2">
            <Clock className="h-4 w-4 text-violet-600" />
            Activité récente
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recentEntries && recentEntries.length > 0 ? (
            <div className="space-y-1">
              <div className="text-lg font-bold text-violet-800">
                {format(new Date(recentEntries[0].date), "d MMMM", { locale: fr })}
              </div>
              <p className="text-xs text-violet-600">
                Dernière entrée il y a {Math.floor((new Date().getTime() - new Date(recentEntries[0].date).getTime()) / (1000 * 60 * 60 * 24))} jours
              </p>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Aucune entrée récente</p>
          )}
        </CardContent>
      </Card>
    </Grid>
  );
};
